import { constants, ethers } from "ethers";
import { markRaw } from "vue";

const TOKEN_ABI = [
  "function name() external view returns (string)",
  "function symbol() external view returns (string)",
  "function decimals() external view returns (uint8)",
  "function totalSupply() external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transfer(address recipient, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)",
];

const UNISWAP_ROUTER_ABI = [
  "function swapExactETHForTokens(uint amountOutMin, address[] calldata path, address to, uint deadline) external payable returns (uint[] memory amounts)",
  "function swapExactTokensForETHSupportingFeeOnTransferTokens(uint amountIn, uint amountOutMin, address[] calldata path, address to, uint deadline)",
  "function getAmountsOut(uint amountIn, address[] calldata path) external view returns (uint[] memory amounts)",
];

const UNISWAP_ROUTER_ABI_V3 = [
  "function exactInputSingle(tuple(address tokenIn, address tokenOut, uint24 fee, address recipient, uint256 deadline, uint256 amountIn, uint256 amountOutMinimum, uint160 sqrtPriceLimitX96)) external payable returns (uint256)",
];

const BASE_UNISWAP_ROUTER_ABI_V3 = [
  "function exactInputSingle(tuple(address tokenIn, address tokenOut, uint24 fee, address recipient, uint256 amountIn, uint256 amountOutMinimum, uint160 sqrtPriceLimitX96)) external payable returns (uint256)",
];

const POOL_ABI = [
  "function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)",
];

// eslint-disable-next-line no-unused-vars
const QUOTER_ABI = [
  "function quoteExactInputSingle(address tokenIn, address tokenOut, uint24 fee, uint256 amountIn, uint160 sqrtPriceLimitX96) external returns (uint256 amountOut)",
];

const UNISWAP_FACTORY_ABI = [
  "function getPair(address tokenA, address tokenB) external view returns (address pair)",
  "function getPool(address tokenA, address tokenB, uint24 fee) external view returns (address)",
  "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
  "function liquidity() external view returns (uint128)",
];

const ethereum = {
  namespaced: true,
  state: {
    provider: null,
    routerContract: null,
    factoryContract: null,
    tokenAddress: "",
    pairAddress: "",
    tokenContract: null,
    rpcUrl: "",
    gasLimit: 300000,
    gasPrice: 3,
    slippage: 5,
    metadata: {
      name: "",
      symbol: "",
      totalSupply: 0,
      decimals: 0,
    },
    tokenPriceInETH: 0,
    uniswapV3: false,
    feeTier: 0,
    fourMeme: false,
  },
  mutations: {
    setProvider(state, payload) {
      state.provider = payload;
    },
    setRouterContract(state, payload) {
      state.routerContract = payload;
    },
    setFactoryContract(state, payload) {
      state.factoryContract = payload;
    },
    setTokenAddress(state, payload) {
      state.tokenAddress = payload;
    },
    setPairAddress(state, payload) {
      state.pairAddress = payload;
    },
    setTokenContract(state, payload) {
      state.tokenContract = payload;
    },
    setRpcUrl(state, payload) {
      state.rpcUrl = payload;
    },
    setGasLimit(state, payload) {
      state.gasLimit = payload;
    },
    setGasPrice(state, payload) {
      state.gasPrice = payload;
    },
    setSlippage(state, payload) {
      state.slippage = payload;
    },
    setMetadata(state, payload) {
      state.metadata = payload;
    },
    setTokenPriceInETH(state, payload) {
      state.tokenPriceInETH = payload;
    },
    setUniswapV3(state, payload) {
      state.uniswapV3 = payload;
    },
    setFeeTier(state, payload) {
      state.feeTier = payload;
    },
    setFourMeme(state, payload) {
      state.fourMeme = payload;
    },
  },
  getters: {
    getDefaultData(state, getters, rootState) {
      return {
        tokenAddress: state.tokenAddress,
        rpcUrl: rootState.network.chain.rpcUrl,
        gasLimit: state.gasLimit,
        gasPrice: state.gasPrice,
        slippage: state.slippage,
      };
    },
    generateWallet() {
      const wallet = ethers.Wallet.createRandom();
      return {
        address: wallet.address,
        privateKey: wallet.privateKey,
      };
    },
    getSinger: (state) => (wallet) => {
      return new ethers.Wallet(wallet.privateKey, state.provider);
    },
    getBalanceNative: (state) => async (address) => {
      return await state.provider.getBalance(address);
    },
    getBalanceToken: (state) => async (address) => {
      return await state.tokenContract.balanceOf(address);
    },
    calculateGasPriceWithIncrement: (state) => async (gasPrice) => {
      return (await state.provider.getGasPrice()).add(
        ethers.utils.parseUnits(gasPrice.toString(), "gwei")
      );
    },
  },
  actions: {
    async init({ state, rootState, getters, rootGetters, commit, dispatch }) {
      const data = Object.assign(
        {},
        getters.getDefaultData,
        rootGetters["network/getLocalStorage"]
      );
      const { tokenAddress, rpcUrl, gasLimit, gasPrice, slippage } = data;
      commit("setRpcUrl", rpcUrl);
      commit("setGasLimit", gasLimit);
      commit("setGasPrice", gasPrice);
      commit("setSlippage", slippage);
      const provider = markRaw(
        new ethers.providers.JsonRpcProvider(state.rpcUrl)
      );
      commit("setProvider", provider);
      const routerContract = markRaw(
        new ethers.Contract(
          rootState.network.chain.routeAddress,
          UNISWAP_ROUTER_ABI,
          provider
        )
      );
      commit("setRouterContract", routerContract);
      const factoryContract = markRaw(
        new ethers.Contract(
          rootState.network.chain.factoryAddress,
          UNISWAP_FACTORY_ABI,
          state.provider
        )
      );
      commit("setFactoryContract", factoryContract);
      if (tokenAddress) {
        await dispatch("setTokenAddress", tokenAddress);
      }
      await dispatch("network/setLocalStorage", data, {
        root: true,
      });
    },
    async setTokenAddress(
      { state, rootState, commit, dispatch },
      tokenAddress
    ) {
      commit("setTokenAddress", tokenAddress);
      if (tokenAddress) {
        const tokenContract = markRaw(
          new ethers.Contract(state.tokenAddress, TOKEN_ABI, state.provider)
        );
        commit("setTokenContract", tokenContract);
        await dispatch("getTokenMetadata");
        if (state.fourMeme) {
          await dispatch("fourMeme/getTokenInfo", null, { root: true });
          if (rootState.fourMeme.liquidityAdded) {
            commit("setFourMeme", false);
            await dispatch("getPairAddress");
            await dispatch("wallet/refreshBalanceTokenAllWallet", null, {
              root: true,
            });
            await dispatch(
              "network/updateLocalStorage",
              { tokenAddress },
              { root: true }
            );
          }
        } else {
          await dispatch("getPairAddress");
          await dispatch("wallet/refreshBalanceTokenAllWallet", null, {
            root: true,
          });
          await dispatch(
            "network/updateLocalStorage",
            { tokenAddress },
            { root: true }
          );
        }
      } else {
        commit("setMetadata", {
          name: "",
          symbol: "",
          totalSupply: 0,
          decimals: 0,
        });
        commit("setPairAddress", "");
        commit("setTokenContract", null);
      }
    },
    async getTokenMetadata({ state, commit }) {
      try {
        const [decimals, name, symbol, totalSupply] = await Promise.all([
          await state.tokenContract.decimals(),
          await state.tokenContract.name(),
          await state.tokenContract.symbol(),
          await state.tokenContract.totalSupply(),
        ]);

        commit("setMetadata", {
          name: name,
          symbol: symbol,
          decimals: decimals,
          totalSupply: Number(ethers.utils.formatUnits(totalSupply, decimals)),
        });
      } catch (e) {
        console.log(e);
        commit(
          "toast/add",
          {
            type: "danger",
            title: "The address is not a valid token contract",
          },
          { root: true }
        );
        commit("setTokenAddress", "");
        commit("setTokenContract", null);
      }
    },
    async getTokenPriceInETH({ state, rootState, commit, dispatch }) {
      try {
        if (state.fourMeme) {
          const tokenPriceInETH = await dispatch("fourMeme/tryBuy", null, {
            root: true,
          });
          commit("setTokenPriceInETH", tokenPriceInETH);
          return;
        }
        if (state.uniswapV3) {
          const pool = new ethers.Contract(
            state.pairAddress,
            POOL_ABI,
            state.provider
          );
          const { sqrtPriceX96 } = await pool.slot0();

          let price = sqrtPriceX96 ** 2 / 2 ** 192;
          if (rootState.network.chain.id === 56) {
            price = 1 / price;
          }
          commit("setTokenPriceInETH", Math.trunc(price * (98.75 / 100)));
          return;
        }
        const amountsOut = await state.routerContract.getAmountsOut(
          ethers.utils.parseUnits("1", "ether"),
          [rootState.network.chain.wrappedAddress, state.tokenAddress]
        );
        commit(
          "setTokenPriceInETH",
          ethers.utils.formatUnits(amountsOut[1], state.metadata.decimals)
        );
      } catch (e) {
        console.log(e);
        commit("setTokenPriceInETH", 0);
      }
    },
    async getPairAddress({ state, rootState, commit, dispatch }) {
      try {
        let pairAddress = await state.factoryContract.getPair(
          rootState.network.chain.wrappedAddress,
          state.tokenAddress
        );
        if (!pairAddress || pairAddress === constants.AddressZero) {
          pairAddress = await state.factoryContract.getPair(
            state.tokenAddress,
            rootState.network.chain.wrappedAddress
          );
        }
        let isActive = false;
        if (pairAddress !== constants.AddressZero) {
          const poolContract = new ethers.Contract(
            pairAddress,
            UNISWAP_FACTORY_ABI,
            state.provider
          );
          const reserves = await poolContract.getReserves();
          isActive = reserves[0] > 0 && reserves[1] > 0;
        }
        if (isActive) {
          commit("setPairAddress", pairAddress);
          commit("setUniswapV3", false);
          return pairAddress;
        }
        await dispatch("getPairAddressV3");
      } catch (e) {
        commit("setUniswapV3", false);
        commit("setPairAddress", "");
        return null;
      }
    },
    async getPairAddressV3({ state, rootState, commit }) {
      try {
        const factoryContract = new ethers.Contract(
          rootState.network.chain.factoryAddressV3,
          UNISWAP_FACTORY_ABI,
          state.provider
        );
        for (const fee of rootState.network.chain.feeTiers) {
          let pairAddress = await factoryContract.getPool(
            rootState.network.chain.wrappedAddress,
            state.tokenAddress,
            fee
          );
          if (!pairAddress || pairAddress === constants.AddressZero) {
            pairAddress = await factoryContract.getPool(
              state.tokenAddress,
              rootState.network.chain.wrappedAddress,
              fee
            );
          }
          if (pairAddress !== constants.AddressZero) {
            const poolContract = new ethers.Contract(
              pairAddress,
              UNISWAP_FACTORY_ABI,
              state.provider
            );
            const liquidity = await poolContract.liquidity();
            if (liquidity > 0) {
              commit("setPairAddress", pairAddress);
              commit("setUniswapV3", true);
              commit("setFeeTier", fee);
              return pairAddress;
            }
          }
        }
        commit("setUniswapV3", false);
        return null;
      } catch (e) {
        commit("setUniswapV3", false);
        commit("setPairAddress", "");
        return null;
      }
    },
    // eslint-disable-next-line no-unused-vars
    async estimateGas({ state }, payload) {
      try {
        const { tx, provider } = payload;
        return await provider.estimateGas(tx);
      } catch (e) {
        console.log(e);
        throw new Error("Estimate gas failed!");
      }
    },
    getRouterABIV3({ rootState }) {
      if (rootState.network.chain.id === 8453) {
        return BASE_UNISWAP_ROUTER_ABI_V3;
      }
      return UNISWAP_ROUTER_ABI_V3;
    },
    async transferBalanceNative({ state, getters, dispatch }, payload) {
      let { wallet, recipientAddress, amount } = payload;
      amount = ethers.utils.parseEther(amount.toString());
      if (amount.lt(0)) {
        throw new Error("Balance must be greater than or equal to 0");
      }
      if (!ethers.utils.isAddress(recipientAddress)) {
        throw new Error("Recipient address is invalid!");
      }
      const signer = getters.getSinger(wallet);
      const balance = await signer.getBalance();
      if (balance.isZero()) {
        throw new Error("Wallet balance is zero. No funds to transfer.");
      }

      const tx = {
        from: signer.address,
        to: recipientAddress,
        value: amount,
      };
      const estimatedGasLimit = await dispatch("estimateGas", {
        tx,
        provider: state.provider,
      });
      const gasPrice = await getters.calculateGasPriceWithIncrement(2);
      const gasCost = gasPrice.mul(estimatedGasLimit);
      if (balance.lte(amount.add(gasCost))) {
        amount = balance.sub(gasCost);
      }
      if (amount.lte(0)) {
        throw new Error("Insufficient balance to cover the gas cost.");
      }
      tx.value = amount;
      tx.gasLimit = estimatedGasLimit;
      tx.gasPrice = gasPrice;
      tx.nonce = await state.provider.getTransactionCount(
        signer.address,
        "latest"
      );
      return await signer.sendTransaction(tx);
    },
    async transferBalanceToken({ state, getters, dispatch }, payload) {
      let { wallet, recipientAddress, amount } = payload;
      amount = ethers.utils.parseUnits(
        amount.toString(),
        state.metadata.decimals
      );
      if (amount.lt(0)) {
        throw new Error("Token balance must be greater than or equal to 0");
      }
      if (!ethers.utils.isAddress(recipientAddress)) {
        throw new Error("Recipient address is invalid!");
      }
      const signer = getters.getSinger(wallet);
      const balance = await signer.getBalance();
      if (balance.isZero()) {
        throw new Error("Wallet balance is zero. No funds to transfer.");
      }
      const tx = {
        to: state.tokenAddress,
        data: state.tokenContract.interface.encodeFunctionData("transfer", [
          recipientAddress,
          amount,
        ]),
      };
      const estimatedGasLimit = await dispatch("estimateGas", {
        tx,
        provider: signer,
      });
      const gasPrice = await getters.calculateGasPriceWithIncrement(2);
      const gasCost = gasPrice.mul(estimatedGasLimit);
      if (balance.lte(amount.add(gasCost))) {
        amount = balance.sub(gasCost);
      }
      if (amount.lte(0)) {
        throw new Error("Insufficient balance to cover the gas cost.");
      }
      tx.gasLimit = estimatedGasLimit;
      tx.gasPrice = gasPrice;
      tx.nonce = await state.provider.getTransactionCount(
        signer.address,
        "latest"
      );
      return await signer.sendTransaction(tx);
    },
    async buy({ state, rootState, getters, dispatch }, payload) {
      if (state.fourMeme) {
        return await dispatch("fourMeme/buy", payload, { root: true });
      }
      if (state.uniswapV3) {
        return await dispatch("buyV3", payload);
      }
      let { wallet, amount } = payload;
      amount = ethers.utils.parseEther(amount.toString());
      const signer = getters.getSinger(wallet);
      const path = [rootState.network.chain.wrappedAddress, state.tokenAddress];
      let amountOutMin = 0;
      try {
        const amountsOut = await state.routerContract.getAmountsOut(
          amount,
          path
        );
        amountOutMin = amountsOut[1].sub(
          amountsOut[1].mul(state.slippage).div(100)
        );
      } catch (e) {
        throw new Error("Insufficient Liquidity in the Pool");
      }
      const tx = {
        to: state.routerContract.address,
        value: amount,
        data: state.routerContract.interface.encodeFunctionData(
          "swapExactETHForTokens",
          [
            amountOutMin,
            path,
            signer.address,
            Math.floor(Date.now() / 1000) + 60 * 10,
          ]
        ),
      };
      const gasLimit = Math.max(
        state.gasLimit,
        await dispatch("estimateGas", { tx, provider: signer })
      );
      const gasPrice = await getters.calculateGasPriceWithIncrement(
        state.gasPrice
      );
      const gasCost = gasPrice.mul(gasLimit);
      const balance = await signer.getBalance();
      if (balance.sub(gasCost).lte(0)) {
        throw new Error("Insufficient balance to cover the gas cost.");
      }
      tx.gasLimit = gasLimit;
      tx.gasPrice = gasPrice;
      tx.nonce = await state.provider.getTransactionCount(
        signer.address,
        "latest"
      );
      return await signer.sendTransaction(tx);
    },
    async buyV3({ state, rootState, getters, dispatch }, payload) {
      const { wallet, amount } = payload;
      const signer = getters.getSinger(wallet);
      const amountIn = ethers.utils.parseEther(amount.toString());
      // eslint-disable-next-line no-unused-vars
      await dispatch("getTokenPriceInETH");
      const amountOut = ethers.utils.parseUnits(
        Math.trunc(
          amount * state.tokenPriceInETH * ((100 - state.slippage) / 100)
        ).toString(),
        state.metadata.decimals
      );
      const ROUTER_ABI_V3 = await dispatch("getRouterABIV3");
      const swapRouter = new ethers.Contract(
        rootState.network.chain.routeAddressV3,
        ROUTER_ABI_V3,
        signer
      );
      const params = {
        tokenIn: rootState.network.chain.wrappedAddress,
        tokenOut: state.tokenAddress,
        fee: state.feeTier,
        recipient: wallet.address,
      };
      if (rootState.network.chain.id !== 8453) {
        params.deadline = Math.floor(Date.now() / 1000) + 60 * 10;
      }
      params.amountIn = amountIn;
      params.amountOutMinimum = amountOut;
      params.sqrtPriceLimitX96 = 0;
      const tx = {
        to: swapRouter.address,
        value: amountIn,
        data: swapRouter.interface.encodeFunctionData("exactInputSingle", [
          params,
        ]),
      };
      const gasLimit = Math.max(
        state.gasLimit,
        await dispatch("estimateGas", { tx, provider: signer })
      );
      const gasPrice = await getters.calculateGasPriceWithIncrement(
        state.gasPrice
      );
      const gasCost = gasPrice.mul(gasLimit);
      const balance = await signer.getBalance();
      if (balance.sub(gasCost).lte(0)) {
        throw new Error("Insufficient balance to cover the gas cost.");
      }
      tx.gasLimit = gasLimit;
      tx.gasPrice = gasPrice;
      tx.nonce = await state.provider.getTransactionCount(
        signer.address,
        "latest"
      );
      return await signer.sendTransaction(tx);
    },
    async sell({ state, rootState, getters, dispatch }, payload) {
      if (state.fourMeme) {
        return await dispatch("fourMeme/sell", payload, { root: true });
      }
      if (state.uniswapV3) {
        return await dispatch("sellV3", payload);
      }
      let { wallet, amount } = payload;
      amount = ethers.utils.parseUnits(
        amount.toString(),
        state.metadata.decimals
      );
      if (amount.lt(0)) {
        throw new Error("Token balance must be greater than or equal to 0");
      }
      const signer = getters.getSinger(wallet);
      const path = [state.tokenAddress, rootState.network.chain.wrappedAddress];
      let amountOutMin = 0;
      try {
        const amountsOut = await state.routerContract.getAmountsOut(
          amount,
          path
        );
        amountOutMin = amountsOut[1].sub(
          amountsOut[1].mul(state.slippage).div(100)
        );
      } catch (e) {
        throw new Error("Insufficient Liquidity in the Pool");
      }
      await dispatch("checkAllowanceAndApprove", wallet);
      const tx = {
        to: state.routerContract.address,
        data: state.routerContract.interface.encodeFunctionData(
          "swapExactTokensForETHSupportingFeeOnTransferTokens",
          [
            amount,
            amountOutMin,
            path,
            signer.address,
            Math.floor(Date.now() / 1000) + 60 * 10,
          ]
        ),
      };
      const gasLimit = Math.max(
        state.gasLimit,
        await dispatch("estimateGas", { tx, provider: signer })
      );
      const gasPrice = await getters.calculateGasPriceWithIncrement(
        state.gasPrice
      );
      const gasCost = gasPrice.mul(gasLimit);
      const balance = await signer.getBalance();
      if (balance.sub(gasCost).lte(0)) {
        throw new Error("Insufficient balance to cover the gas cost.");
      }
      tx.gasLimit = gasLimit;
      tx.gasPrice = gasPrice;
      tx.nonce = await state.provider.getTransactionCount(
        signer.address,
        "latest"
      );
      return await signer.sendTransaction(tx);
    },
    async sellV3({ state, rootState, getters, dispatch }, payload) {
      const { wallet, amount } = payload;
      const signer = getters.getSinger(wallet);
      const amountIn = ethers.utils.parseUnits(
        amount.toString(),
        state.metadata.decimals
      );
      if (amountIn.lt(0)) {
        throw new Error("Token balance must be greater than or equal to 0");
      }
      await dispatch("checkAllowanceAndApprove", wallet);
      const ROUTER_ABI_V3 = await dispatch("getRouterABIV3");
      const swapRouter = new ethers.Contract(
        rootState.network.chain.routeAddressV3,
        ROUTER_ABI_V3,
        signer
      );
      const params = {
        tokenIn: state.tokenAddress,
        tokenOut: rootState.network.chain.wrappedAddress,
        fee: state.feeTier,
        recipient: wallet.address,
      };
      if (rootState.network.chain.id !== 8453) {
        params.deadline = Math.floor(Date.now() / 1000) + 60 * 10;
      }
      params.amountIn = amountIn;
      params.amountOutMinimum = 0;
      params.sqrtPriceLimitX96 = 0;
      const tx = {
        to: swapRouter.address,
        data: swapRouter.interface.encodeFunctionData("exactInputSingle", [
          params,
        ]),
      };
      const gasLimit = Math.max(
        state.gasLimit,
        await dispatch("estimateGas", { tx, provider: signer })
      );
      const gasPrice = await getters.calculateGasPriceWithIncrement(
        state.gasPrice
      );
      const gasCost = gasPrice.mul(gasLimit);
      const balance = await signer.getBalance();
      if (balance.sub(gasCost).lte(0)) {
        throw new Error("Insufficient balance to cover the gas cost.");
      }
      tx.gasLimit = gasLimit;
      tx.gasPrice = gasPrice;
      tx.nonce = await state.provider.getTransactionCount(
        signer.address,
        "latest"
      );
      return await signer.sendTransaction(tx);
    },
    async checkAllowanceAndApprove({ state, rootState, getters }, wallet) {
      const signer = getters.getSinger(wallet);
      let isApprove = false;
      let routerAddress = state.routerContract.address;
      if (state.fourMeme) {
        routerAddress = rootState.fourMeme.tokenManagerAddress;
      } else if (state.uniswapV3) {
        routerAddress = rootState.network.chain.routeAddressV3;
      }
      try {
        const allowanceAmount = await state.tokenContract.allowance(
          signer.address,
          routerAddress
        );
        isApprove = !!allowanceAmount.gt(0);
        // eslint-disable-next-line no-empty
      } catch (e) {}
      if (!isApprove) {
        const tx = {
          to: state.tokenAddress,
          data: state.tokenContract.interface.encodeFunctionData("approve", [
            routerAddress,
            ethers.constants.MaxUint256,
          ]),
          gasLimit: 100000,
        };
        const transaction = await signer.sendTransaction(tx);
        await transaction.wait();
      }
    },
    async unwrapWETH({ rootState, getters }, wallet) {
      const signer = getters.getSinger(wallet);
      const wethContract = new ethers.Contract(
        rootState.network.chain.wrappedAddress,
        [
          "function balanceOf(address owner) view returns (uint256)",
          "function withdraw(uint256) public",
        ],
        signer
      );
      const balance = await wethContract.balanceOf(wallet.address);
      if (balance.eq(0)) {
        return;
      }
      const tx = await wethContract.withdraw(balance);
      await tx.wait();
    },
  },
};

export default ethereum;
